/**
 * userCatalogStore — learner catalog slice (Zustand).
 *
 *   GET /api/learners/categories        GET /api/learners/courses
 *   GET /api/learners/courses/:id       GET /api/learners/courses/:id/related
 *   GET /api/learners/books
 *   GET /api/learners/books/:id         GET /api/learners/journals
 *   GET /api/learners/journals/:id      GET /api/learners/search
 */
import { apiRequest, normalizeList } from "./userUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const userCatalogInitialState = {
  userCategories: [],
  userCategoriesLoading: false,
  userCategoriesError: null,

  userCourses: [],
  userCoursesLoading: false,
  userCoursesError: null,
  userCoursesTotal: 0,
  userCoursesPages: 1,

  userCourseDetail: null,
  userCourseDetailLoading: false,
  userCourseDetailError: null,

  userRelatedCourses: [],
  userRelatedCoursesLoading: false,
  userRelatedCoursesError: null,
  userRelatedCoursesTotal: 0,
  userRelatedCoursesPages: 1,

  userBooks: [],
  userBooksLoading: false,
  userBooksError: null,
  userBooksTotal: 0,
  userBooksPages: 1,

  userBookDetail: null,
  userBookDetailLoading: false,
  userBookDetailError: null,

  userJournals: [],
  userJournalsLoading: false,
  userJournalsError: null,
  userJournalsTotal: 0,
  userJournalsPages: 1,

  userJournalDetail: null,
  userJournalDetailLoading: false,
  userJournalDetailError: null,

  userSearchResults: [],
  userSearchLoading: false,
  userSearchError: null,
};

export function createUserCatalogActions(set, get) {
  async function fetchList(prefix, path, params = {}, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const query = new URLSearchParams(params).toString();
      const data = await apiRequest(get, `${path}${query ? `?${query}` : ""}`);
      const { items, total, totalPages } = normalizeList(data, keys);
      set({
        [`${prefix}Loading`]: false,
        [prefix]: items,
        [`${prefix}Total`]: total,
        [`${prefix}Pages`]: totalPages,
      });
      return { success: true, items, total, totalPages };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  async function fetchDetail(prefix, path, keys = []) {
    set({ [`${prefix}Loading`]: true, [`${prefix}Error`]: null });
    try {
      const data = await apiRequest(get, path);
      const payload = data?.data ?? (keys.length ? keys.reduce((a, k) => a ?? data?.[k], undefined) : undefined) ?? data;
      set({ [`${prefix}Loading`]: false, [prefix]: payload });
      return { success: true, data: payload };
    } catch (err) {
      const msg = toDisplayString(err.message, "Request failed");
      set({ [`${prefix}Loading`]: false, [`${prefix}Error`]: msg });
      return { success: false, error: msg };
    }
  }

  return {
    fetchUserCategories: (params = {}) =>
      fetchList("userCategories", "/api/learners/categories", params, ["categories"]),

    fetchUserCourses: (params = {}) =>
      fetchList("userCourses", "/api/learners/courses", params, ["courses"]),

    fetchUserCourse: (id) =>
      fetchDetail("userCourseDetail", `/api/learners/courses/${id}`, ["course"]),

    // GET /api/learners/courses/:id/related
    fetchUserRelatedCourses: (id, params = {}) =>
      fetchList(
        "userRelatedCourses",
        `/api/learners/courses/${id}/related`,
        params,
        ["courses", "relatedCourses", "items"]
      ),

    fetchUserBooks: (params = {}) =>
      fetchList("userBooks", "/api/learners/books", params, ["books"]),

    fetchUserBook: (id) => fetchDetail("userBookDetail", `/api/learners/books/${id}`, ["book"]),

    fetchUserJournals: (params = {}) =>
      fetchList("userJournals", "/api/learners/journals", params, ["journals"]),

    fetchUserJournal: (id) =>
      fetchDetail("userJournalDetail", `/api/learners/journals/${id}`, ["journal"]),

    // GET /api/learners/search?q=…
    fetchUserSearch: (params = {}) =>
      fetchList("userSearchResults", "/api/learners/search", params, [
        "courses",
        "books",
        "journals",
        "results",
      ]),
  };
}

const userCatalogActions = createUserCatalogActions;
export default userCatalogActions;
